import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  REPORT_MAX_RANGE_DAYS,
  REPORT_PRESETS,
  ReportArea,
  ReportPreset,
  type OpticalReportQuery,
} from '@optiflow/types';
import { reportAreaVisible } from '@optiflow/shared';
import { Alert } from '@/components/ui/Alert';
import { Card } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { apiErrorCode, apiErrorDetails } from '@/services/apiClient';
import { useAuthStore } from '@/store/authStore';
import { useOpticalReportQuery } from '../api/customersOptical.api';
import { OpticalContent } from '../components/OpticalContent';
import { ReportFilterBar, type ReportFilters } from '../components/ReportFilterBar';
import { ReportMetaLine } from '../components/ReportMetaLine';
import { ReportTabs } from '../components/ReportTabs';

const PERIOD_FIELDS = new Set(['preset', 'from', 'to']);

/** The filters this page keeps in the URL, so a reading can be shared or reloaded unchanged. */
function readFilters(params: URLSearchParams) {
  const preset = params.get('preset');
  return {
    period: {
      preset: (REPORT_PRESETS.includes(preset as ReportPreset)
        ? preset
        : ReportPreset.THIS_MONTH) as ReportPreset,
      from: params.get('from') ?? '',
      to: params.get('to') ?? '',
      branchId: params.get('branch') ?? '',
      compare: 'none' as const,
    } satisfies ReportFilters,
    labId: params.get('lab') ?? '',
  };
}

/**
 * Reports › Optical.
 *
 * Current jobs are as of now whatever the period; the period narrows lifecycle history only, so the
 * filter bar offers no comparison. The lab filter is an id in the URL, resolved — and refused — by
 * the server.
 */
export function OpticalReportPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const filters = readFilters(params);
  const { period } = filters;
  const grants = useAuthStore((state) => state.user?.permissions);
  const allowed = reportAreaVisible(grants, ReportArea.OPTICAL);
  const customReady =
    period.preset !== ReportPreset.CUSTOM || (period.from !== '' && period.to !== '');

  const query: OpticalReportQuery = {
    preset: period.preset,
    ...(period.preset === ReportPreset.CUSTOM ? { from: period.from, to: period.to } : {}),
    branchId: period.branchId || undefined,
    labId: filters.labId || undefined,
  };
  const report = useOpticalReportQuery(query, { enabled: customReady && allowed });
  const data = report.data;

  const write = (next: Partial<ReturnType<typeof readFilters>>) => {
    const merged = { ...filters, ...next, period: { ...period, ...(next.period ?? {}) } };
    const search = new URLSearchParams();
    search.set('preset', merged.period.preset);
    if (merged.period.preset === ReportPreset.CUSTOM) {
      if (merged.period.from) search.set('from', merged.period.from);
      if (merged.period.to) search.set('to', merged.period.to);
    }
    if (merged.period.branchId) search.set('branch', merged.period.branchId);
    if (merged.labId) search.set('lab', merged.labId);
    setParams(search, { replace: true });
  };

  const errorText = () => {
    const code = apiErrorCode(report.error);
    if (code === 'FORBIDDEN') return t('reports.optical.noAccess');
    if (code !== 'VALIDATION_FAILED') return t('reports.loadError');
    const fields = apiErrorDetails(report.error).map((detail) => detail.field ?? '');
    return fields.some((name) => PERIOD_FIELDS.has(name))
      ? t('reports.invalidPeriod', { days: REPORT_MAX_RANGE_DAYS })
      : t('reports.optical.invalidFilter');
  };

  const labs = (data?.byLab ?? []).flatMap((row) =>
    row.labId ? [{ id: row.labId, name: row.labName ?? row.labId }] : [],
  );

  return (
    <div className="space-y-5">
      <PageHeader title={t('reports.optical.title')} subtitle={t('reports.optical.subtitle')} />
      <ReportTabs />
      {allowed ? null : <Alert tone="info">{t('reports.optical.noAccess')}</Alert>}
      {allowed ? (
        <>
          <ReportFilterBar
            value={period}
            onChange={(next) => write({ period: { ...next, compare: 'none' } })}
            hideCompare
          />
          {labs.length > 0 || filters.labId ? (
            <Card className="p-4">
              <label htmlFor="optical-lab" className="text-muted mb-1 block text-xs font-medium">
                {t('reports.optical.filters.lab')}
              </label>
              <select
                id="optical-lab"
                className="border-line bg-surface text-ink w-full max-w-xs rounded-lg border px-3 py-2 text-sm"
                value={filters.labId}
                onChange={(event) => write({ labId: event.target.value })}
              >
                <option value="">{t('reports.optical.filters.allLabs')}</option>
                {labs.map((lab) => (
                  <option key={lab.id} value={lab.id}>
                    {lab.name}
                  </option>
                ))}
                {filters.labId && !labs.some((lab) => lab.id === filters.labId) ? (
                  <option value={filters.labId}>{data?.filters.labName ?? filters.labId}</option>
                ) : null}
              </select>
            </Card>
          ) : null}
        </>
      ) : null}
      {data ? <ReportMetaLine meta={data.meta} /> : null}
      {!customReady && allowed ? (
        <Alert tone="info">{t('reports.filters.chooseDates')}</Alert>
      ) : null}
      {report.isError ? <Alert tone="error">{errorText()}</Alert> : null}
      {report.isPending && customReady && allowed ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-hidden>
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="bg-surface border-line h-28 animate-pulse rounded-xl border"
            />
          ))}
        </div>
      ) : null}
      {data ? <OpticalContent report={data} query={query} /> : null}
    </div>
  );
}
